import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';

// Placeholders qui n'existent PAS dans le code de l'application et doivent
// être confirmés par l'exploitant avant mise en production juridique.
// Ils sont volontairement rendus visibles (styles amber) afin qu'un oubli
// ne passe pas inaperçu.
const UNKNOWN_PRIVACY_EMAIL = '[à compléter — adresse e-mail de contact confidentialité]';
const UNKNOWN_DPO = '[à compléter — coordonnées DPO/référent RGPD si désigné]';
const LAST_UPDATE = '22 février 2026';

function Missing({ label }) {
  return (
    <span
      className="inline-block px-1.5 py-0.5 rounded-sm bg-amber-500/10 border border-amber-500/40 text-amber-300 text-[11px] font-mono"
      title="Information non renseignée dans le code de l'application. À compléter par l'administrateur."
    >
      {label}
    </span>
  );
}

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-amber-400 transition-colors"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Retour à ProdPilot
          </Link>
          <div className="flex items-center gap-2 text-zinc-100">
            <ShieldCheck className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-semibold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              ProdPilot
            </span>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-8">
        <div>
          <h1
            className="text-3xl sm:text-4xl font-bold text-zinc-50 tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Politique de confidentialité
          </h1>
          <p className="text-xs text-zinc-500 mt-2">Dernière mise à jour : {LAST_UPDATE}</p>
        </div>

        <section className="text-sm text-zinc-300 leading-relaxed">
          <p>
            La présente politique de confidentialité décrit la manière dont ProdPilot
            (ci-après « ProdPilot » ou « le Service »), plateforme professionnelle
            exploitée par la société ProdLink UE SAS, collecte, utilise, protège et
            partage vos données personnelles lorsque vous utilisez le Service, y compris
            lorsque vous connectez votre compte Google/Gmail. Elle a pour objectif de vous
            fournir une information claire et transparente sur nos pratiques et sur vos
            droits, conformément au Règlement (UE) 2016/679 (RGPD) et à la loi
            Informatique et Libertés.
          </p>
        </section>

        {/* 1. Responsable du traitement */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">1. Responsable du traitement</h2>
          <div className="text-sm text-zinc-300 leading-relaxed space-y-1">
            <p>Le responsable du traitement des données personnelles est :</p>
            <ul className="list-none pl-0 space-y-0.5 text-zinc-400 text-[13px]">
              <li>ProdLink UE SAS</li>
              <li>Les Voyettes, 60430 Silly-Tillard, France</li>
              <li>SIRET : 98473615700012</li>
              <li>Site du service : prodpilots.com</li>
              <li>Contact confidentialité : <Missing label={UNKNOWN_PRIVACY_EMAIL} /></li>
              <li>Référent RGPD : <Missing label={UNKNOWN_DPO} /></li>
            </ul>
          </div>
        </section>

        {/* 2. Données collectées */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">2. Données collectées</h2>
          <div className="text-sm text-zinc-300 leading-relaxed space-y-2">
            <p>ProdPilot collecte uniquement les données nécessaires au fonctionnement du Service :</p>
            <ul className="list-disc pl-5 space-y-1 text-[13px] text-zinc-300">
              <li>
                <strong className="text-zinc-100">Compte utilisateur :</strong> nom, adresse
                e-mail professionnelle, mot de passe (stocké de manière chiffrée / hashé),
                rôle (administrateur, commercial, sous-traitant, client) et préférences
                d'interface.
              </li>
              <li>
                <strong className="text-zinc-100">Données professionnelles saisies dans le
                Service :</strong> fiches clients, fiches sous-traitants, projets, demandes de
                prix, devis, paiements, expéditions, contrôles qualité, tickets, prospects
                de démarchage et pièces jointes (plans PDF, images, fichiers STEP, DWG, etc.).
              </li>
              <li>
                <strong className="text-zinc-100">Données Google (uniquement si vous
                connectez votre compte) :</strong> voir la section dédiée ci-dessous.
              </li>
              <li>
                <strong className="text-zinc-100">Données techniques :</strong> journaux
                d'accès applicatifs limités (identifiant utilisateur, horodatage, action
                réalisée) nécessaires à la sécurité, à la détection d'anomalies et au bon
                fonctionnement du Service.
              </li>
            </ul>
          </div>
        </section>

        {/* 3. Connexion Google/Gmail */}
        <section className="space-y-3" data-testid="privacy-google-section">
          <h2 className="text-lg font-semibold text-zinc-100">
            3. Connexion volontaire à votre compte Google / Gmail
          </h2>
          <div className="text-sm text-zinc-300 leading-relaxed space-y-2">
            <p>
              La connexion d'un compte Google/Gmail à ProdPilot est <strong>strictement
              volontaire</strong>. Elle n'est ni requise pour créer un compte ni pour
              utiliser les fonctionnalités principales de ProdPilot.
            </p>
            <p>
              Lorsque vous choisissez de connecter votre compte Google, ProdPilot vous
              redirige vers l'écran de consentement officiel de Google. ProdPilot ne peut
              accéder à vos données Gmail <strong>qu'après votre autorisation
              explicite</strong> sur cet écran.
            </p>
            <p className="pt-1">
              <strong className="text-zinc-100">Autorisations Google demandées</strong>
              (limitées au strict nécessaire pour les fonctionnalités du Service) :
            </p>
            <ul className="list-disc pl-5 space-y-1 text-[13px] text-zinc-300">
              <li>
                <code className="text-amber-300 text-[12px]">gmail.readonly</code> — lire
                vos e-mails Gmail afin de les afficher dans ProdPilot (consultation des
                réponses des sous-traitants et prospects).
              </li>
              <li>
                <code className="text-amber-300 text-[12px]">gmail.modify</code> et
                <code className="text-amber-300 text-[12px]"> gmail.labels</code> — marquer
                des e-mails comme lus/traités et créer un libellé (« ProdPilot/Traité »)
                pour organiser les échanges liés au Service, sans supprimer vos messages.
              </li>
              <li>
                <code className="text-amber-300 text-[12px]">gmail.send</code> — envoyer
                des e-mails depuis ProdPilot à votre demande (envoi de devis, relances,
                réponses aux prospects).
              </li>
              <li>
                <code className="text-amber-300 text-[12px]">openid</code>,
                <code className="text-amber-300 text-[12px]"> userinfo.email</code>,
                <code className="text-amber-300 text-[12px]"> userinfo.profile</code> —
                identifier le compte Google connecté et afficher votre adresse dans
                l'interface de gestion.
              </li>
            </ul>
            <p className="pt-1">
              <strong className="text-zinc-100">Finalité :</strong> ces autorisations sont
              utilisées <strong>uniquement</strong> pour permettre à l'utilisateur de
              consulter ses e-mails dans ProdPilot et d'envoyer des e-mails depuis
              ProdPilot dans le cadre de son activité professionnelle. ProdPilot ne
              demande <strong>aucune autorisation Google qui ne serait pas strictement
              nécessaire</strong> à ces fonctionnalités.
            </p>
          </div>
        </section>

        {/* 4. Utilisation des données Google — Limited Use */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">
            4. Utilisation des données Google — Politique Limited Use
          </h2>
          <div className="text-sm text-zinc-300 leading-relaxed space-y-2">
            <p>
              L'utilisation par ProdPilot des informations reçues des API Google
              respecte la <em>Google API Services User Data Policy</em>, y compris
              les exigences <em>Limited Use</em>. En particulier :
            </p>
            <ul className="list-disc pl-5 space-y-1 text-[13px] text-zinc-300">
              <li>Vos données Google ne sont <strong>ni vendues, ni louées</strong>.</li>
              <li>
                Vos données Google ne sont <strong>jamais utilisées à des fins
                publicitaires</strong>, ni pour du profilage, ni pour de la publicité
                ciblée.
              </li>
              <li>
                Vos données Google ne sont <strong>pas partagées avec des tiers</strong>,
                sauf dans les cas strictement nécessaires au fonctionnement du Service
                (par exemple : notre fournisseur d'infrastructure hébergeant les serveurs
                sur lesquels tourne ProdPilot), à la sécurité du Service, ou pour
                respecter une obligation légale.
              </li>
              <li>
                Les données Gmail ne sont <strong>pas utilisées</strong> pour entraîner
                un modèle d'intelligence artificielle générique ni un modèle utilisé
                au bénéfice d'autres utilisateurs.
              </li>
              <li>
                Les données Gmail ne sont <strong>ni transférées, ni lues par des
                opérateurs humains</strong>, sauf : (a) avec votre consentement explicite,
                (b) pour des besoins de sécurité (par ex. investigation d'un abus), (c)
                pour respecter une obligation légale, ou (d) pour des opérations
                internes strictement nécessaires (support technique demandé par
                l'utilisateur) avec des données anonymisées ou agrégées.
              </li>
            </ul>
          </div>
        </section>

        {/* 5. Finalités */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">5. Finalités des traitements</h2>
          <ul className="list-disc pl-5 space-y-1 text-[13px] text-zinc-300">
            <li>Fourniture des fonctionnalités du Service (production, devis, paiements, expéditions, portail sous-traitants et clients).</li>
            <li>Authentification, gestion des comptes et des sessions.</li>
            <li>Envoi et consultation d'e-mails via l'API Gmail (uniquement pour les utilisateurs ayant connecté leur compte).</li>
            <li>Assistance à la rédaction et à l'analyse via des fonctionnalités d'intelligence artificielle (résumés de plans, extraction de devis, rédaction d'e-mails).</li>
            <li>Sécurité du Service, détection et prévention des fraudes ou abus.</li>
            <li>Respect de nos obligations légales et comptables.</li>
          </ul>
        </section>

        {/* 6. Base légale */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">6. Bases légales</h2>
          <ul className="list-disc pl-5 space-y-1 text-[13px] text-zinc-300">
            <li><strong className="text-zinc-100">Exécution du contrat</strong> : fourniture du Service souscrit par l'utilisateur (art. 6.1.b RGPD).</li>
            <li><strong className="text-zinc-100">Consentement</strong> : connexion à un compte Google/Gmail, envoi d'e-mails commerciaux (art. 6.1.a RGPD).</li>
            <li><strong className="text-zinc-100">Intérêt légitime</strong> : sécurité du Service, détection d'abus, amélioration produit (art. 6.1.f RGPD).</li>
            <li><strong className="text-zinc-100">Obligation légale</strong> : conservation des documents comptables et fiscaux (art. 6.1.c RGPD).</li>
          </ul>
        </section>

        {/* 7. Destinataires */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">7. Destinataires et sous-traitants</h2>
          <div className="text-sm text-zinc-300 leading-relaxed space-y-2">
            <p>
              Les données ne sont accessibles qu'aux personnes habilitées au sein
              de ProdLink UE SAS et aux sous-traitants techniques strictement
              nécessaires à l'exploitation du Service :
            </p>
            <ul className="list-disc pl-5 space-y-1 text-[13px] text-zinc-300">
              <li>Fournisseur d'infrastructure hébergeant les serveurs et la base de données de ProdPilot.</li>
              <li>Google LLC (API Gmail) — pour les utilisateurs ayant connecté leur compte.</li>
              <li>Fournisseur d'API d'intelligence artificielle (Anthropic — modèle Claude) pour les analyses de plans et la rédaction d'e-mails, sur les seules données strictement nécessaires à la requête.</li>
              <li>Fournisseur d'envoi d'e-mails transactionnels (Resend) pour les notifications applicatives.</li>
            </ul>
            <p>
              Chaque sous-traitant est tenu à des obligations contractuelles de
              confidentialité et de sécurité conformes au RGPD.
            </p>
          </div>
        </section>

        {/* 8. Stockage et sécurité + jetons OAuth */}
        <section className="space-y-3" data-testid="privacy-security-section">
          <h2 className="text-lg font-semibold text-zinc-100">
            8. Stockage et sécurité — jetons OAuth Google
          </h2>
          <div className="text-sm text-zinc-300 leading-relaxed space-y-2">
            <p>
              Les données applicatives sont stockées dans une base de données MongoDB
              hébergée sur l'infrastructure de notre fournisseur cloud (Union
              européenne / Espace économique européen selon la configuration). Les
              fichiers téléversés (plans, PDF, images) sont stockés sur un service de
              stockage objet dédié à l'application. Les échanges entre votre
              navigateur et ProdPilot sont chiffrés en transit via TLS (HTTPS).
            </p>
            <p>
              <strong className="text-zinc-100">Jetons OAuth Google :</strong> lorsque
              vous connectez votre compte Google, les jetons d'accès (<em>access token</em>)
              et de rafraîchissement (<em>refresh token</em>) fournis par Google sont
              stockés dans la base de données du Service, dans une collection dédiée
              accessible uniquement par les composants serveur de ProdPilot. Ces jetons
              ne sont jamais exposés côté navigateur ni transmis à des tiers autres que
              Google. L'accès aux jetons est restreint aux opérations techniques
              indispensables à l'appel de l'API Gmail pour votre compte.
            </p>
            <p>
              Les mots de passe utilisateurs sont hashés (jamais stockés en clair). Les
              accès administrateurs à l'infrastructure sont limités et journalisés.
            </p>
          </div>
        </section>

        {/* 9. Durée de conservation */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">9. Durée de conservation</h2>
          <ul className="list-disc pl-5 space-y-1 text-[13px] text-zinc-300">
            <li>Données de compte : pendant toute la durée d'utilisation du Service, puis supprimées ou anonymisées dans un délai raisonnable après clôture du compte.</li>
            <li>Données professionnelles (devis, factures, échanges commerciaux) : conservées pour la durée requise par les obligations comptables et fiscales applicables (généralement 10 ans en France pour les pièces comptables).</li>
            <li>Jetons OAuth Google : conservés tant que la connexion Google est active. Ils sont supprimés lorsque vous révoquez l'accès (voir section 11) ou lorsque vous supprimez votre compte.</li>
            <li>Journaux techniques et de sécurité : conservés pour une durée limitée strictement nécessaire à la détection d'incidents.</li>
          </ul>
          <p className="text-[12px] text-zinc-500 italic">
            Les durées exactes peuvent être précisées sur demande à l'adresse de contact confidentialité.
          </p>
        </section>

        {/* 10. Droits RGPD */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">10. Vos droits (RGPD)</h2>
          <div className="text-sm text-zinc-300 leading-relaxed space-y-2">
            <p>
              Conformément au RGPD, vous disposez d'un droit d'accès, de rectification,
              d'effacement, de limitation, d'opposition et de portabilité de vos données,
              ainsi que du droit de définir des directives relatives au sort de vos
              données après votre décès. Vous pouvez également retirer à tout moment
              votre consentement lorsque le traitement repose sur celui-ci.
            </p>
            <p>
              Pour exercer ces droits, contactez-nous à l'adresse indiquée en section 1.
              Vous disposez enfin du droit d'introduire une réclamation auprès de la CNIL
              (<a
                href="https://www.cnil.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-400 hover:text-amber-300 underline"
              >cnil.fr</a>).
            </p>
          </div>
        </section>

        {/* 11. Révocation Google + suppression */}
        <section className="space-y-3" data-testid="privacy-revocation-section">
          <h2 className="text-lg font-semibold text-zinc-100">
            11. Révocation de l'accès Google et suppression des données
          </h2>
          <div className="text-sm text-zinc-300 leading-relaxed space-y-2">
            <p>Vous pouvez à tout moment :</p>
            <ul className="list-disc pl-5 space-y-1 text-[13px] text-zinc-300">
              <li>
                <strong className="text-zinc-100">Révoquer l'accès de ProdPilot à votre
                compte Google</strong> depuis la page dédiée de Google :{' '}
                <a
                  href="https://myaccount.google.com/permissions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-400 hover:text-amber-300 underline"
                >
                  https://myaccount.google.com/permissions
                </a>
                . La révocation invalide immédiatement les jetons OAuth côté Google, et
                ProdPilot ne pourra plus accéder à votre boîte Gmail.
              </li>
              <li>
                <strong className="text-zinc-100">Déconnecter votre compte Google depuis
                ProdPilot</strong> via la page « Mail » de l'application (bouton de
                déconnexion Gmail). Cette action supprime les jetons OAuth stockés dans
                notre base de données.
              </li>
              <li>
                <strong className="text-zinc-100">Demander la suppression de vos données
                personnelles</strong> en écrivant à l'adresse de contact confidentialité
                indiquée en section 1. Nous supprimerons ou anonymiserons les données
                dont la conservation n'est pas requise par une obligation légale
                (par ex. pièces comptables).
              </li>
            </ul>
          </div>
        </section>

        {/* 12. Cookies */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">12. Cookies et stockage local</h2>
          <div className="text-sm text-zinc-300 leading-relaxed space-y-2">
            <p>
              ProdPilot n'utilise <strong>pas de cookies publicitaires ni de traceurs
              tiers</strong>. Le Service utilise le <em>localStorage</em> du navigateur
              pour stocker le jeton d'authentification technique (JWT) nécessaire au
              maintien de votre session, ainsi que quelques préférences d'interface
              (langue, affichage). Ces données restent locales à votre navigateur.
            </p>
          </div>
        </section>

        {/* 13. Transferts hors UE */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">13. Transferts hors de l'Union européenne</h2>
          <div className="text-sm text-zinc-300 leading-relaxed">
            <p>
              Certaines fonctionnalités reposent sur des sous-traitants dont
              l'infrastructure peut être située hors de l'Union européenne (par
              exemple Google LLC pour l'API Gmail, Anthropic pour les fonctions d'IA).
              Ces transferts sont encadrés par les mécanismes prévus par le RGPD
              (clauses contractuelles types ou tout autre mécanisme équivalent) et
              limités aux données strictement nécessaires.
            </p>
          </div>
        </section>

        {/* 14. Modifications */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">14. Modification de la présente politique</h2>
          <div className="text-sm text-zinc-300 leading-relaxed">
            <p>
              ProdPilot peut être amené à mettre à jour cette politique de
              confidentialité pour refléter des évolutions du Service, de la
              réglementation ou de ses sous-traitants. La date de dernière mise à jour
              indiquée en haut de la page permet de connaître la version en vigueur.
            </p>
          </div>
        </section>

        {/* 15. Contact */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">15. Contact</h2>
          <div className="text-sm text-zinc-300 leading-relaxed">
            <p>
              Pour toute question relative à cette politique de confidentialité, à
              l'exercice de vos droits ou à la protection des données au sein de
              ProdPilot, contactez-nous à l'adresse suivante :{' '}
              <Missing label={UNKNOWN_PRIVACY_EMAIL} />.
            </p>
          </div>
        </section>

        {/* Warning bloc pour l'admin */}
        <section
          className="mt-8 p-4 border border-amber-500/30 bg-amber-500/5 rounded-sm text-xs text-amber-200"
          data-testid="privacy-admin-warning"
        >
          <strong className="text-amber-300">Note administrateur :</strong> les zones
          marquées <Missing label="[à compléter…]" /> ne sont pas encore renseignées dans
          le code de l'application. Merci de fournir l'adresse e-mail de contact
          confidentialité et, le cas échéant, les coordonnées du DPO/référent RGPD
          avant de publier l'URL de cette page dans l'écran de consentement OAuth
          de Google Cloud.
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-[11px] text-zinc-500">
          <span>© {new Date().getFullYear()} ProdLink UE SAS — ProdPilot</span>
          <Link to="/login" className="hover:text-amber-400 transition-colors" data-testid="privacy-footer-login-link">
            Accéder à ProdPilot →
          </Link>
        </div>
      </footer>
    </div>
  );
}
